import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

export type LoopErrorSource = 'tool_execution' | 'sse_envelope';

export type LoopErrorClass =
  | 'artifact_not_found'
  | 'unknown_tool'
  | 'user_cancelled'
  | 'cancelled'
  | 'unexpected'
  | 'json'
  | 'schema';

/**
 * Recognise an aborted/cancelled operation: an `AbortError` /
 * `APIUserAbortError` (OpenAI SDK), a `DOMException` abort, or a thrown value
 * whose message indicates the user or stream aborted the turn.
 */
function isCancellation(err: unknown): boolean {
  if (err instanceof Error) {
    if (err.name === 'AbortError' || err.name === 'APIUserAbortError') {
      return true;
    }
    if (/\babort(?:ed)?\b/i.test(err.message)) {
      return true;
    }
  }
  if (
    typeof DOMException !== 'undefined' &&
    err instanceof DOMException &&
    err.name === 'AbortError'
  ) {
    return true;
  }
  return false;
}

/**
 * Map a tool failure — either a thrown value from `executeTool` or an
 * `ERROR: …` string the client tool returned — to a categorical class for
 * telemetry. Thrown `Error`s are opaque (`unexpected`); the recognised
 * `ERROR:` strings come from `global-ai-client-tools.ts`.
 */
export function classifyToolError(err: unknown): LoopErrorClass {
  if (isCancellation(err)) return 'cancelled';
  if (err instanceof Error) return 'unexpected';
  const msg = typeof err === 'string' ? err : '';
  if (/artifact .* not found|no filter artifact/i.test(msg))
    return 'artifact_not_found';
  if (/is not a client tool|unknown tool/i.test(msg)) return 'unknown_tool';
  if (/cancel/i.test(msg)) return 'user_cancelled';
  return 'unexpected';
}

/**
 * Fire-and-forget POST of a categorical loop-error report to the BE. Best
 * effort — transport failures are swallowed so telemetry never affects the
 * turn's control flow.
 */
export async function reportLoopError(report: {
  source: LoopErrorSource;
  toolName?: string;
  errorClass: LoopErrorClass;
  stepIndex?: number;
}): Promise<void> {
  try {
    await fetch(
      `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/log-turn-error`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-surf-token-dash-meta': getAuthDashMetaCsrfToken() ?? '',
        },
        body: JSON.stringify(report),
      }
    );
  } catch {
    // Telemetry is best-effort — never let it affect the turn.
  }
}
